/**
 * The notification bell in the top bar (DESIGN §4.2): unread count on the bell, a panel
 * of the latest 20 with the unread ones marked, "Mark all read", and each row opening the
 * screen it is about (proof to check → the queue, new or inactive client → their
 * profile, plan ending → their Workout tab, message → Messages).
 *
 * Desktop: a 384 px panel under the bell. Phone (375 px): the panel spans the screen
 * under the top bar. Escape or a click outside closes it.
 */
import { timeAgo } from '@gymcoach/shared';
import { Button, cn, Skeleton } from '@gymcoach/ui';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useMe } from '@/auth/useMe';

import { copy } from './copy';
import { BELL_LIMIT, type BellNotification, useBell, useMarkAllRead, useMarkRead } from './data';
import { crmLinkFor } from './links';

type Tone = 'info' | 'warning' | 'accent' | 'neutral';

const TONE_CLASSES: Record<Tone, string> = {
  info: 'bg-info-subtle text-info',
  warning: 'bg-warning-subtle text-warning',
  accent: 'bg-accent-subtle text-accent',
  neutral: 'bg-surface-sunken text-ink-muted',
};

const GLYPHS: Record<string, { path: string; tone: Tone }> = {
  payment_submitted: { path: 'M3 6h18v12H3zM3 10h18M7 15h4', tone: 'info' },
  client_assigned: { path: 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M2 21c0-3.9 3.1-7 7-7s7 3.1 7 7M19 8v6M16 11h6', tone: 'accent' },
  client_inactive: { path: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M12 6v6l4 2', tone: 'warning' },
  plan_ending: { path: 'M4 5h16v16H4zM4 9h16M9 3v4M15 3v4M9 14h6', tone: 'warning' },
  chat_message: { path: 'M4 5h16v11H9l-5 4z', tone: 'accent' },
};
const DEFAULT_GLYPH = { path: 'M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10 21a2 2 0 0 0 4 0', tone: 'neutral' as Tone };

function Glyph({ type }: { type: string }) {
  const glyph = GLYPHS[type] ?? DEFAULT_GLYPH;
  return (
    <span className={cn('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full', TONE_CLASSES[glyph.tone])}>
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={glyph.path} />
      </svg>
    </span>
  );
}

function BellIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10 21a2 2 0 0 0 4 0" />
    </svg>
  );
}

function Row({ n, onOpen }: { n: BellNotification; onOpen: (n: BellNotification, href: string | null) => void }) {
  const { role } = useMe();
  const href = crmLinkFor(n, role);
  const unread = !n.read_at;
  const content = (
    <>
      <Glyph type={n.type} />
      <span className="min-w-0 flex-1">
        <span className={cn('block truncate text-sm', unread ? 'font-semibold text-ink' : 'font-medium text-ink-muted')}>{n.title}</span>
        <span className="line-clamp-2 block text-sm text-ink-muted">{n.body}</span>
        <span className="mt-0.5 block text-xs text-ink-subtle">{timeAgo(n.created_at)}</span>
      </span>
      {unread ? (
        <span className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full bg-primary" role="img" aria-label={copy.unread} />
      ) : (
        <span className="w-2.5 shrink-0" aria-hidden="true" />
      )}
    </>
  );
  const classes = cn(
    'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-sunken focus-visible:bg-surface-sunken focus-visible:outline-none',
    unread && 'bg-primary-subtle/40',
  );
  return (
    <li>
      {href ? (
        <Link
          to={href}
          className={classes}
          data-testid="bell-item"
          onClick={(event) => {
            event.preventDefault();
            onOpen(n, href);
          }}
        >
          {content}
        </Link>
      ) : (
        <button type="button" className={classes} data-testid="bell-item" onClick={() => onOpen(n, null)}>
          {content}
        </button>
      )}
    </li>
  );
}

export function NotificationBell() {
  const { isAdmin } = useMe();
  const bell = useBell();
  const markRead = useMarkRead();
  const markAll = useMarkAllRead();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const unread = bell.data?.unread ?? 0;
  const items = bell.data?.items ?? [];
  const { refetch } = bell;

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    void refetch();
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) close(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close(true);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close, refetch]);

  const onOpen = (n: BellNotification, href: string | null) => {
    if (!n.read_at) markRead.mutate(n.id);
    if (href) {
      close(false);
      navigate(href);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <Button
        ref={buttonRef}
        variant="ghost"
        size="icon"
        aria-label={copy.button(unread)}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-haspopup="dialog"
        onClick={() => setOpen((o) => !o)}
        className="relative"
        data-testid="bell"
      >
        <BellIcon />
        {unread > 0 ? (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-danger px-1 text-[11px] font-semibold leading-none text-white ring-2 ring-surface"
            aria-hidden="true"
            data-testid="bell-count"
          >
            {unread > 99 ? copy.overflow : unread}
          </span>
        ) : null}
      </Button>

      {open ? (
        <div
          id={panelId}
          role="dialog"
          aria-label={copy.title}
          className="fixed inset-x-2 top-14 z-40 flex max-h-[calc(100dvh-4.5rem)] flex-col overflow-hidden rounded-lg border bg-surface shadow-overlay sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 sm:max-h-[32rem]"
          data-testid="bell-panel"
        >
          <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
            <h2 className="text-base font-semibold text-ink">{copy.title}</h2>
            <Button
              variant="ghost"
              size="sm"
              disabled={unread === 0 || markAll.isPending}
              onClick={() => markAll.mutate()}
              data-testid="bell-mark-all"
            >
              {markAll.isPending ? copy.markingAll : copy.markAll}
            </Button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {bell.isPending ? (
              <ul aria-busy="true" className="divide-y">
                {[0, 1, 2].map((i) => (
                  <li key={i} className="flex gap-3 px-4 py-3">
                    <Skeleton circle className="h-8 w-8" />
                    <span className="flex-1 space-y-2">
                      <Skeleton className="h-3.5 w-2/3" />
                      <Skeleton className="h-3 w-full" />
                    </span>
                  </li>
                ))}
              </ul>
            ) : bell.isError && items.length === 0 ? (
              <div className="space-y-3 px-4 py-8 text-center">
                <p className="text-sm text-ink-muted">{copy.loadError}</p>
                <Button variant="secondary" size="sm" onClick={() => void refetch()}>
                  {copy.retry}
                </Button>
              </div>
            ) : items.length === 0 ? (
              <div className="px-6 py-10 text-center" data-testid="bell-empty">
                <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-accent-subtle text-accent">
                  <BellIcon />
                </span>
                <p className="text-sm font-semibold text-ink">{copy.emptyTitle}</p>
                <p className="mt-1 text-sm text-ink-muted">{copy.emptyBody(isAdmin)}</p>
              </div>
            ) : (
              <ul className="divide-y">
                {items.map((n) => (
                  <Row key={n.id} n={n} onOpen={onOpen} />
                ))}
              </ul>
            )}
          </div>
          {items.length >= BELL_LIMIT ? (
            <p className="border-t px-4 py-2 text-center text-xs text-ink-subtle">{copy.more(BELL_LIMIT)}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
